import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import Modal from "../../../UI/Modal";
import Snackbar from "../../../UI/Snackbar";
import {
  buildRegiPayload,
  validateReplyNumber,
} from "../../../lib/mfBulkInput/buildRegiPayload";
import { exportRowsToExcel } from "../../../lib/mfBulkInput/exportRowsToExcel";
import { validateRows } from "../../../lib/mfBulkInput/validateRows";
import { applyServerDuplicateCheck } from "../../../lib/mfBulkInput/validateRowsAsync";
import BulkInputFooter from "./BulkInputFooter";
import BulkInputGrid from "./BulkInputGrid";
import { BulkInputHelpModal } from "./BulkInputHelpFab";
import BulkInputMessagePanel from "./BulkInputMessagePanel";
import BulkInputModeCards from "./BulkInputModeCards";
import BulkInputStepper from "./BulkInputStepper";
import BulkInputToolbar, { useRdvBootstrap } from "./BulkInputToolbar";
import BulkInputValidationPanel from "./BulkInputValidationPanel";
import useBulkInputState from "./useBulkInputState";
import useQrPhoneFill from "./useQrPhoneFill";
import cls from "./MflistExcelinput.module.css";

export default function MflistExcelinput(props) {
  const { apiAddr, onClose, onSelectDS } = props;
  const state = useBulkInputState(apiAddr);
  const {
    mode,
    setMode,
    rows,
    visibleRows,
    uploadedFileName,
    showErrorsOnly,
    setShowErrorsOnly,
    isValidating,
    isSaving,
    setIsSaving,
    saveProgress,
    setSaveProgress,
    validationCounts,
    activeStep,
    trainCount,
    setTrainCount,
    trainRdvs,
    setTrainRdvs,
    includeNonFire,
    setIncludeNonFire,
    updateField,
    deleteRow,
    clearRows,
    loadExcelFile,
    runValidation,
    runAutoCorrect,
    generateTraining,
    saveTrainConditions,
    fillRandomPhones,
    clearPhoneFields,
    applyQrEntries,
    setRows,
    syncSessionStorage,
    setSavedAt,
  } = state;

  const { rdvList, dsRdvRows, dsLocationHint } = useRdvBootstrap(apiAddr);
  const [showAllTrainRdvs, setShowAllTrainRdvs] = useState(false);
  const [message, setMessage] = useState("");
  const [replyNumber, setReplyNumber] = useState("033-249-5664");
  const [showHelp, setShowHelp] = useState(false);
  const [sbShow, setSbShow] = useState("");
  const rowRefs = useRef({});

  const { qrSession, qrFilled, qrDebug, openQrModal } = useQrPhoneFill(
    apiAddr,
    applyQrEntries,
  );

  const callSnackbar = (msg, sort) => {
    setSbShow(`${Date.now()}^${msg}^${sort}`);
  };

  const scrollToRow = useCallback((rowId) => {
    rowRefs.current[rowId]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const handleRandomPhones = () => {
    const n = fillRandomPhones();
    callSnackbar(
      n > 0 ? `빈 선탑전화번호 ${n}건 랜덤 입력` : "랜덤 입력할 빈 칸 없음",
      n > 0 ? "success" : "info",
    );
  };

  const handleQrPhones = () => {
    openQrModal();
  };

  const canSaveProduction =
    mode === "production" &&
    rows.length > 0 &&
    validationCounts.errors === 0 &&
    !isSaving &&
    state.validatedAt;

  const canExportDemo = mode === "training" && rows.length > 0;

  const sendMessageOnce = async () => {
    const dsid = sessionStorage.ondsid;
    const msgData = {
      dsid,
      msg: message,
      replyNumber,
      hqCode: (localStorage.hq || "").trim(),
    };
    await axios.post(`${apiAddr}/post/sendmessage`, msgData);
  };

  const sendSms = async (regiData) => {
    const smsData = {
      ...regiData,
      hqCode: (localStorage.hq || "").trim(),
      replyNumber,
    };
    await axios.post(`${apiAddr}/post/sendsms`, smsData);
  };

  const handleSaveProduction = async () => {
    const replyCheck = validateReplyNumber(replyNumber);
    if (!replyCheck.ok) {
      alert(replyCheck.message);
      return;
    }

    let validated = validateRows(rows);
    const dsid = (sessionStorage.ondsid || "").trim();
    validated = await applyServerDuplicateCheck(validated, apiAddr, dsid);
    setRows(validated);
    syncSessionStorage(validated);

    const errCount = validated.filter((r) => r.rowStatus === "error").length;
    if (errCount > 0) {
      alert(`오류 ${errCount}건이 있습니다. 검증 후 다시 시도하세요.`);
      return;
    }

    if (!window.confirm("입력하신 내용을 실데이터로 저장·문자 발송하시겠습니까?")) {
      return;
    }

    setIsSaving(true);
    setSaveProgress({ done: 0, total: validated.length });

    try {
      if (message.trim()) await sendMessageOnce();

      const snumRes = await axios.get(
        `${apiAddr}/get/getsnum?dsid=${encodeURIComponent(dsid)}`,
      );
      let seqnum = Number(snumRes.data[0]?.seqnum || 0);

      for (let i = 0; i < validated.length; i += 1) {
        const row = validated[i];
        seqnum += 1;
        const regiData = buildRegiPayload(row, { dsid, seqnum });

        try {
          const res = await axios.post(`${apiAddr}/post/mfinputall`, regiData);
          if (res.data.result === "success") {
            await sendSms(regiData);
            validated[i] = { ...row, saveStatus: "saved", rowStatus: "normal" };
          } else {
            validated[i] = { ...row, saveStatus: "failed" };
          }
        } catch {
          validated[i] = { ...row, saveStatus: "failed" };
        }
        setSaveProgress({ done: i + 1, total: validated.length });
        setRows([...validated]);
        syncSessionStorage(validated);
      }

      setSavedAt(Date.now());
      callSnackbar("저장이 완료되었습니다.", "success");
      onSelectDS?.();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} modalSort="mfinput">
      <Snackbar sbShow={sbShow} />
      <div className={cls.root}>
        <header className={cls.header}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#ff7043",
              fontSize: "1.1rem",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            ✕
          </button>
          <div>
            <span className={cls.headerTitle}>동원자원 일괄입력</span>
            <span className={cls.modeBadge}>
              {mode === "training" ? "훈련모드" : "실데이터 모드"}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            style={{
              background: "none",
              border: "1.5px solid rgba(255,255,255,0.6)",
              borderRadius: "50%",
              width: 24,
              height: 24,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            ?
          </button>
        </header>

        <BulkInputModeCards mode={mode} setMode={setMode} />

        <BulkInputToolbar
          mode={mode}
          apiAddr={apiAddr}
          rdvList={rdvList}
          dsRdvRows={dsRdvRows}
          dsLocationHint={dsLocationHint}
          showAllTrainRdvs={showAllTrainRdvs}
          setShowAllTrainRdvs={setShowAllTrainRdvs}
          uploadedFileName={uploadedFileName}
          onSelectFile={loadExcelFile}
          onValidate={runValidation}
          isValidating={isValidating}
          trainCount={trainCount}
          setTrainCount={setTrainCount}
          includeNonFire={includeNonFire}
          setIncludeNonFire={setIncludeNonFire}
          trainRdvs={trainRdvs}
          setTrainRdvs={setTrainRdvs}
          onGenerate={generateTraining}
          onClear={() => {
            if (window.confirm("입력된 모든 행을 삭제할까요?")) clearRows();
          }}
          onSaveConditions={saveTrainConditions}
        />

        <BulkInputStepper mode={mode} activeStep={activeStep} />

        <BulkInputMessagePanel
          message={message}
          setMessage={setMessage}
          replyNumber={replyNumber}
          setReplyNumber={setReplyNumber}
          apiAddr={apiAddr}
        />

        {qrSession && (
          <div className={cls.qrDebug}>
            [QR] session: {qrSession} | 등록: {qrFilled}건
            {qrDebug.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}

        <div className={cls.mainBody}>
          <BulkInputValidationPanel
            rows={rows}
            validationCounts={validationCounts}
            showErrorsOnly={showErrorsOnly}
            setShowErrorsOnly={setShowErrorsOnly}
            onScrollToRow={scrollToRow}
          />
          <div className={cls.gridWrap}>
            <BulkInputGrid
              rows={visibleRows}
              mode={mode}
              updateField={updateField}
              deleteRow={deleteRow}
              onClearPhones={() => {
                if (window.confirm("선탑자명·전화번호를 초기화할까요?")) {
                  clearPhoneFields();
                }
              }}
              onRandomPhones={handleRandomPhones}
              onQrPhones={handleQrPhones}
              rowRefs={rowRefs}
            />
          </div>
        </div>

        <BulkInputFooter
          mode={mode}
          validationCounts={validationCounts}
          isSaving={isSaving}
          saveProgress={saveProgress}
          canSaveProduction={canSaveProduction}
          canExportDemo={canExportDemo}
          onSaveProduction={handleSaveProduction}
          onExportDemo={() => exportRowsToExcel(rows)}
          onAutoCorrect={runAutoCorrect}
          onClose={onClose}
        />
      </div>

      <BulkInputHelpModal
        open={showHelp}
        onClose={() => setShowHelp(false)}
        mode={mode}
      />
    </Modal>
  );
}
